import React, { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { AttachFile, BugReport, Close, HeadsetMic } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { useSnackbar } from 'notistack';

import { ApiFailure, clearFailure } from 'apiFailures';
import { createTicket, getWidgetUrl } from './api';
import { FILE_EXTENSIONS, FILES_ACCEPT, FILES_HINT, MAX_FILE_SIZE, MAX_FILES, MESSAGE_LIMIT } from './constants';

type SupportTab = 'report' | 'tickets';

interface SupportDrawerProps {
  open: boolean;
  failure: ApiFailure | null;
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 720,
    maxWidth: '100vw',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
  },
  title: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
  },
  form: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  files: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  fileInput: {
    display: 'none',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  tickets: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  frame: {
    flexGrow: 1,
    width: '100%',
    border: 'none',
  },
  loader: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alert: {
    margin: theme.spacing(2),
  },
}));

const messageOf = (error: unknown, fallback: string): string =>
  (error as AxiosError<{ message?: string }>).response?.data?.message || fallback;

const pathOf = (url: string): string => {
  try {
    return new URL(url, globalThis.location.origin).pathname;
  } catch {
    return url;
  }
};

const titleOf = ({ status, method, url }: ApiFailure): string => {
  const what = status ? `Ошибка ${status}` : 'Сервер не ответил';

  return `${what}: ${method} ${pathOf(url)}`;
};

const keyOf = (file: File): string => `${file.name}:${file.size}:${file.lastModified}`;

const rejectionOf = (file: File): string | null => {
  const dot = file.name.lastIndexOf('.');
  const extension = dot < 0 ? '' : file.name.slice(dot + 1).toLowerCase();

  if (!FILE_EXTENSIONS.includes(extension)) return 'Такой тип файла приложить нельзя';
  if (file.size > MAX_FILE_SIZE) return 'Файл больше 10 МБ';

  return null;
};

const SupportDrawer = ({ open, failure, onClose }: SupportDrawerProps) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState<SupportTab>('report');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [filesError, setFilesError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [widgetUrl, setWidgetUrl] = useState('');
  const [widgetError, setWidgetError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || tab !== 'tickets' || widgetUrl) return;

    let isStale = false;

    setWidgetError('');
    getWidgetUrl()
      .then((url) => {
        if (isStale) return;
        // Во фрейм попадает только https-адрес: значение приходит из настроек стенда.
        if (new URL(url).protocol !== 'https:') throw new Error('Недопустимый адрес виджета');

        setWidgetUrl(url);
      })
      .catch((error) => {
        if (!isStale) setWidgetError(messageOf(error, 'Не удалось открыть обращения. Попробуйте позже.'));
      });

    return () => {
      isStale = true;
    };
  }, [open, tab, widgetUrl]);

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files || []);
    const rejected = picked.find((file) => rejectionOf(file));
    const accepted = picked.filter(
      (file) => !rejectionOf(file) && !files.some((attached) => keyOf(attached) === keyOf(file)),
    );
    const next = [...files, ...accepted];

    // Иначе повторный выбор того же файла после удаления не вызовет onChange.
    event.target.value = '';

    if (rejected) setFilesError(`${rejectionOf(rejected)}: ${rejected.name}`);
    else if (next.length > MAX_FILES) setFilesError(`Можно приложить не больше ${MAX_FILES} файлов`);
    else setFilesError('');

    setFiles(next.slice(0, MAX_FILES));
  };

  const handleSubmit = async () => {
    setIsSending(true);
    setSendError('');

    try {
      const { attachmentsFailed } = await createTicket(message, failure, files);

      setMessage('');
      setFiles([]);
      setFilesError('');
      clearFailure();

      if (attachmentsFailed) {
        enqueueSnackbar('Заявка отправлена, но файлы прикрепить не удалось. Добавьте их в открытой заявке.', {
          variant: 'warning',
          autoHideDuration: 10000,
        });
        setTab('tickets');
        return;
      }

      enqueueSnackbar('Заявка отправлена в техподдержку. Ответ появится в «Мои обращения».', { variant: 'success' });
      onClose();
    } catch (error) {
      setSendError(messageOf(error, 'Не удалось отправить заявку. Попробуйте позже.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ className: classes.paper }}>
      <Box className={classes.header}>
        <HeadsetMic color="primary" />
        <Typography variant="h6" className={classes.title}>
          Поддержка
        </Typography>
        <IconButton size="small" aria-label="Закрыть" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
        onChange={(_, value: SupportTab) => setTab(value)}
      >
        <Tab value="report" label="Сообщить об ошибке" />
        <Tab value="tickets" label="Мои обращения" />
      </Tabs>
      <Divider />

      {tab === 'report' && (
        <>
          <Box className={classes.form}>
            <Typography variant="body2">
              Опишите, что вы делали и что пошло не так. Заявка уйдёт в техподдержку вместе с техническими данными сбоя.
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Канал только для сбоев системы: вопросы по заявкам, актам и согласованиям здесь не решаются.
            </Typography>

            {failure && (
              <Alert
                severity="error"
                icon={<BugReport />}
                action={
                  <Button color="inherit" size="small" onClick={clearFailure}>
                    Не прикладывать
                  </Button>
                }
              >
                {titleOf(failure)}
                {failure.requestId && (
                  <Typography variant="caption" display="block">
                    Идентификатор запроса: {failure.requestId}
                  </Typography>
                )}
              </Alert>
            )}

            <TextField
              multiline
              id="support-message"
              minRows={5}
              variant="outlined"
              label="Что случилось"
              value={message}
              helperText={`${message.length} / ${MESSAGE_LIMIT}`}
              inputProps={{ maxLength: MESSAGE_LIMIT }}
              onChange={(event) => setMessage(event.target.value)}
            />

            <Box>
              <Box className={classes.files}>
                <Button variant="outlined" startIcon={<AttachFile />} onClick={() => fileInputRef.current?.click()}>
                  Скриншоты и файлы
                </Button>
                <input
                  multiple
                  ref={fileInputRef}
                  type="file"
                  tabIndex={-1}
                  className={classes.fileInput}
                  accept={FILES_ACCEPT}
                  onChange={handleFilesChange}
                />
                {files.map((file) => (
                  <Chip
                    key={keyOf(file)}
                    label={file.name}
                    onDelete={() => setFiles(files.filter((attached) => attached !== file))}
                  />
                ))}
              </Box>
              <Typography variant="caption" color={filesError ? 'error' : 'textSecondary'}>
                {filesError || FILES_HINT}
              </Typography>
            </Box>

            {sendError && <Alert severity="error">{sendError}</Alert>}
          </Box>

          <Divider />
          <Box className={classes.actions}>
            <Button onClick={onClose}>Отмена</Button>
            <Button variant="contained" color="primary" disabled={!message.trim() || isSending} onClick={handleSubmit}>
              {isSending ? 'Отправка…' : 'Отправить'}
            </Button>
          </Box>
        </>
      )}

      {tab === 'tickets' && (
        <Box className={classes.tickets}>
          {widgetUrl && (
            <iframe
              src={widgetUrl}
              title="Мои обращения в техподдержку"
              className={classes.frame}
              allow="clipboard-write"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
            />
          )}
          {!widgetUrl && widgetError && (
            <Alert severity="error" className={classes.alert}>
              {widgetError}
            </Alert>
          )}
          {!widgetUrl && !widgetError && (
            <Box className={classes.loader}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default SupportDrawer;
